import { Body2 } from 'ui/theme';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { selectIsFrequencyManageOptimizeFlagOn } from 'core/userContext';
import { useEditStateContext } from 'modules/frequencyManagement/contexts/EditStateContext';
import { useCoreContext } from 'modules/frequencyManagement/contexts/CoreContext';
import { useFiltersContext } from 'modules/frequencyManagement/contexts/FiltersContext';
import { SearchableText } from 'ui-beta/searchableText';
import { EngagementMetrics } from 'modules/frequencyManagement/components/engagementMetrics/EngagementMetrics';
import { useOptimizeModeTotal } from 'modules/frequencyManagement/hooks/useOptimizeModeTotal';
import { createRootName } from 'modules/frequencyManagement/commonUtils';
import { FrequencyMode, SupportedMediums } from 'modules/frequencyManagement/lib/types';
import {
  FrequencyModeSelect,
  createMessageTypeSelectOptions,
} from '../frequencyModeSelect/FrequencyModeSelect';
import { FixedCap } from '../fixedCap/FixedCap';
import {
  Wrapper,
  Header,
  Left,
  FlexWrapWithGap,
  CaptionLight,
  InlineFlex,
  Body2BoldNoMargin,
} from './MessageTypeRow.styled';
import { MessageTypeSchema, MODE } from '../../contexts/FormProvider';
import { Optimize } from '../optimize/Optimize';
import { SummaryCaption } from './SummaryCaption';

export const MessageTypeRow = ({
  messageType,
  messageMedium,
}: {
  messageType: MessageTypeSchema;
  messageMedium: SupportedMediums;
}) => {
  const isOptimizeEnabled = useSelector(selectIsFrequencyManageOptimizeFlagOn);

  const { id, index, name, channelName } = messageType;

  const { getOptimizeDetailsMessageType } = useCoreContext();

  const { search } = useFiltersContext();
  const { isEditingMessageType, createSetIsEditingMessageType } = useEditStateContext();
  const { isAtOptimizeLimit } = useOptimizeModeTotal(messageMedium);

  const isEditing = isEditingMessageType(id);
  const setIsEditingMessageType = createSetIsEditingMessageType(id);

  const rootName = createRootName(index);
  const mode = useWatch({ name: `${rootName}.${MODE}` });

  return (
    <Wrapper>
      <Header>
        <Left>
          <FlexWrapWithGap>
            <Body2BoldNoMargin>
              <SearchableText searchText={search} text={name} />
            </Body2BoldNoMargin>
            <EngagementMetrics
              isOptimizeEnabled={isOptimizeEnabled}
              messageMedium={messageMedium}
              messageTypeId={id}
            />
          </FlexWrapWithGap>
          <InlineFlex>
            <CaptionLight>
              ID: <SearchableText searchText={search} text={id.toString()} />
            </CaptionLight>
            <span>•</span>
            <CaptionLight>
              <SearchableText searchText={search} text={channelName} />
            </CaptionLight>
            {!isEditing && (
              <>
                <span>•</span>
                <SummaryCaption messageMedium={messageMedium} messageType={messageType} />
              </>
            )}
          </InlineFlex>
        </Left>
        <div>
          <FrequencyModeSelect
            rootName={rootName}
            options={createMessageTypeSelectOptions(
              messageMedium,
              name,
              isOptimizeEnabled,
              isAtOptimizeLimit && mode !== FrequencyMode.Optimize,
            )}
            isEditing={isEditing}
            setIsEditing={setIsEditingMessageType}
          />
        </div>
      </Header>
      {/* If Off, don't show */}
      {mode === FrequencyMode.NoCap && isEditing && (
        <Body2>Messages of this type will never be skipped</Body2>
      )}
      {mode === FrequencyMode.Cap && isEditing && (
        <FixedCap rootName={rootName} messageMedium={messageMedium} />
      )}
      {mode === FrequencyMode.Optimize && (
        <Optimize
          isEditing={isEditing}
          rootName={rootName}
          optimizeDetails={getOptimizeDetailsMessageType(messageMedium, id)}
        />
      )}
    </Wrapper>
  );
};
